import { deleteAllLocalData } from '@petrhythm/data';
import { clearInbox } from './share-inbox.js';

/** Remove every product-owned local persistence surface after explicit user or
 * platform disconnect confirmation. Remote ciphertext is handled separately
 * because it can require a live authenticated request. */
export async function deleteAllDeviceData(): Promise<void> {
  await purgeDevicePersistence(false);
}

/** Explicit record-only purge: the UI says the existing anonymous online
 * connection remains. Never use this for full-device deletion or disconnect. */
export async function deleteLocalRecordsKeepOnlineConnection(): Promise<void> {
  await purgeDevicePersistence(true);
}

async function purgeDevicePersistence(keepOnlineConnection: boolean): Promise<void> {
  const complete: string[] = []; const failed: string[] = [];
  const step = async (label: string, task: () => Promise<void>) => { try { await task(); complete.push(label); } catch { failed.push(label); } };
  const purgeEpoch = crypto.randomUUID();
  // Fence already-running decrypt/import work before the serialized DB purge.
  await step('동기화 취소', async () => { localStorage.setItem('petrhythm.purge_epoch', purgeEpoch); localStorage.removeItem('petrhythm.share_state'); });
  await step('기록·사진·기기 백업', deleteAllLocalData);
  await step('공유받은 임시 파일', clearInbox);
  await step('기기 설정', async () => {
    // Storage key order may change after removal. Snapshot names before mutating.
    if ('sessionStorage' in globalThis) for (const key of Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index))) { if (key?.startsWith('petrhythm.')) sessionStorage.removeItem(key); }
    for (const key of Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))) {
      const keep = key === 'petrhythm.purge_epoch' || (keepOnlineConnection && (key === 'petrhythm.install_id' || key === 'petrhythm.subject_token'));
      if (key?.startsWith('petrhythm.') && !keep) localStorage.removeItem(key);
    }
    // Fence sessions that began during DB cleanup while the old identity still existed.
    // Rotate synchronously with identity removal, before awaiting cache cleanup.
    if (!keepOnlineConnection) localStorage.setItem('petrhythm.purge_epoch', crypto.randomUUID());
  });
  await step('앱 임시 저장', async () => {
    if ('caches' in globalThis) { const names = await caches.keys(); await Promise.all(names.filter((name) => name.startsWith('petrhythm-')).map((name) => caches.delete(name))); }
  });
  await step('다른 탭의 초안 삭제', async () => { try { localStorage.setItem('petrhythm.purge_epoch', `${failed.length ? 'purge-failed' : keepOnlineConnection ? 'records-purged' : 'purged'}:${crypto.randomUUID()}`); } finally { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('petrhythm:device-purged')); } });
  if (failed.length) throw new Error(`일부 삭제만 확인했어요. 완료: ${complete.join(', ') || '없음'}. 다시 확인할 항목: ${failed.join(', ')}. 삭제 버튼으로 다시 시도해 주세요.`);
}

/** Survives the provider remount and accurately describes the confirmed purge scope. */
export function devicePurgeFeedback(marker: string): string {
  if (marker.startsWith('records-purged:')) return '기록·사진·기기 백업을 삭제했어요. 온라인 연결과 암호화 백업은 남아 있어요. 백업 암호를 입력하고 불러와 복원해 주세요.';
  if (marker.startsWith('purged:')) return '이 기기의 기록·백업·설정을 삭제했어요.';
  if (marker.startsWith('purge-failed:')) return '삭제를 모두 확인하지 못했어요. 설정에서 남은 데이터를 확인하고 다시 시도해 주세요.';
  return '';
}
